import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { type Impostazioni, useParametri, useSalvaParametri } from '../api/parametri.js';
import { t } from '../i18n/lingua.js';

/**
 * I parametri dell'azienda.
 *
 * Nel v2 la maschera ha 78 voci; qui ne restano cinque, e non è una
 * semplificazione: **tutto il resto erano nomi, chiavi e indirizzi dei Cloud
 * Connector di InDe**, che nella 3.0 sono configurazione del servizio e non
 * righe di database. Una password non deve stare in una tabella che si
 * esporta.
 *
 * Quelle che restano le cambia qualcuno davvero — la riga della SPA è stata
 * toccata il 2 febbraio 2026 — e finora nella 3.0 si potevano cambiare solo
 * con una `UPDATE` a mano.
 *
 * Sono per azienda: SPA e Inc hanno prefissi articolo diversi (`APL` solo la
 * SPA), sezionali diversi e valute diverse. Quello che si vede qui è
 * dell'azienda in cui si sta lavorando, e si cambia dalla barra in alto.
 */
export function Parametri() {
  const dati = useParametri();
  const salva = useSalvaParametri();
  const [modifiche, setModifiche] = useState<Partial<Impostazioni>>({});

  if (dati.isPending) return <div className="cantiere">{t('Un momento…')}</div>;

  /**
   * Il livello lo controlla il servizio. Se chi guarda non è amministratore
   * la richiesta torna 403, e va detto: la voce nel menu c'è per tutti, e una
   * pagina bianca farebbe pensare a un guasto.
   */
  if (dati.error) {
    const messaggio =
      dati.error instanceof ErroreApi
        ? dati.error.message
        : t('Non è stato possibile aprire i parametri.');
    return (
      <>
        <div className="page-head">
          <div>
            <h1 className="page-title">{t('Parametri')}</h1>
          </div>
        </div>
        <div className="cantiere">
          <h2>{t('Questa pagina non è per te')}</h2>
          <p>{messaggio}</p>
        </div>
      </>
    );
  }

  const p = dati.data!;
  const valore = <K extends keyof Impostazioni>(campo: K): Impostazioni[K] =>
    modifiche[campo] !== undefined ? (modifiche[campo] as Impostazioni[K]) : p.settings[campo];
  // rimesso il valore di prima non c'è niente da salvare: prima «modifiche
  // non salvate» restava, e «Salva» acceso
  const cambia = <K extends keyof Impostazioni>(campo: K, v: Impostazioni[K]) =>
    setModifiche((m) => {
      const { [campo]: _tolto, ...altre } = m;
      return v === p.settings[campo] ? altre : { ...altre, [campo]: v };
    });

  const daSalvare = Object.keys(modifiche).length > 0;
  const errore = salva.error instanceof ErroreApi ? salva.error : null;

  /** Il campo vuoto vuol dire «non impostato», cioè `null`, non `''`. */
  const testo = (campo: keyof Impostazioni) => (valore(campo) as string | null) ?? '';
  const scrivi = (campo: keyof Impostazioni) => (v: string) =>
    cambia(campo, (v.trim().length === 0 ? null : v) as never);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Parametri')}</h1>
          <div className="page-sub">
            {p.company ? `${p.company.name} · ${p.company.code}` : t('Nessuna azienda')}
          </div>
        </div>
        <div className="page-actions">
          {daSalvare && <span className="badge badge-warn">{t('modifiche non salvate')}</span>}
          <button
            className="btn btn-primary"
            disabled={!daSalvare || salva.isPending}
            onClick={() => salva.mutate(modifiche, { onSuccess: () => setModifiche({}) })}
          >
            {salva.isPending ? t('Salvo…') : t('Salva')}
          </button>
        </div>
      </div>

      {errore && (
        <div className="avviso" role="alert" style={{ margin: '12px 20px 0' }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Codici a barre GS1')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <div className="field">
                <label htmlFor="pa-us">{t('Prefisso US')}</label>
                <input
                  className="input num"
                  id="pa-us"
                  inputMode="numeric"
                  value={testo('barcodePrefixUs')}
                  onChange={(e) => scrivi('barcodePrefixUs')(e.target.value)}
                />
                <span className="hint">{t('Solo cifre. Entra nel GTIN-12 e nei due GTIN-14 americani.')}</span>
              </div>
              <div className="field">
                <label htmlFor="pa-eu">{t('Prefisso EU')}</label>
                <input
                  className="input num"
                  id="pa-eu"
                  inputMode="numeric"
                  value={testo('barcodePrefixEu')}
                  onChange={(e) => scrivi('barcodePrefixEu')(e.target.value)}
                />
                <span className="hint">{t('Solo cifre. Entra nel GTIN-13 e nei due GTIN-14 europei.')}</span>
              </div>
            </div>
            {/* La cosa che nessuno si aspetta, e che va detta prima e non
                dopo: i codici già generati restano quelli che sono. */}
            <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
              {t('Cambiarli non tocca i codici già generati: valgono per quelli che si generano da adesso in poi. Senza questi due prefissi la generazione dei codici non parte affatto.')}
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Esportazione CSV')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <div className="field span-2">
                <label>{t('Separatore')}</label>
                <div className="scelte">
                  {[
                    { valore: ';', etichetta: t('Punto e virgola') },
                    { valore: ',', etichetta: t('Virgola') },
                  ].map((s) => (
                    <button
                      key={s.valore}
                      className="preset"
                      aria-pressed={valore('csvDelimiter') === s.valore}
                      onClick={() => cambia('csvDelimiter', s.valore)}
                    >
                      <span className="num">{s.valore}</span> {s.etichetta}
                    </button>
                  ))}
                </div>
                <span className="hint">
                  {t('Vale per tutte le esportazioni CSV degli elenchi. I file Excel non cambiano.')}
                </span>
              </div>
            </div>
            <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
              {t('Excel apre il file secondo le impostazioni del computer che lo apre: a Scafati va il punto e virgola, a Edison la virgola. Il v2 esporta con la virgola per tutte e due le società.')}
            </p>
          </div>
        </div>

        <div className="card span-2">
          <div className="card-head">
            <span className="card-title">{t('Avvisi degli import')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <div className="field span-2">
                <label>{t('A chi scrivere quando un import si rompe')}</label>
                <input
                  className="input"
                  type="text"
                  placeholder="mario@laregina.com, ufficio@laregina.com"
                  value={testo('importAlertEmails')}
                  onChange={(e) => scrivi('importAlertEmails')(e.target.value)}
                />
                <span className="hint">
                  {t('Più indirizzi separati da virgola. Lasciandolo vuoto non viene avvisato nessuno.')}
                </span>
              </div>
            </div>
            {/* Il punto non è la lista di indirizzi: è che senza, gli import
                possono restare fermi per settimane come nel v2. */}
            <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
              {t('Parte un messaggio quando un import smette di funzionare, e uno quando riprende. Non uno per ogni tentativo: se resta rotto, si tace finché non cambia qualcosa. Nel v2 gli articoli della Inc erano fermi da due anni e non lo sapeva nessuno.')}
            </p>
          </div>
        </div>

        <div className="card span-2">
          <div className="card-head">
            <span className="card-title">{t('Import da Business Central')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <div className="field span-2">
                <label htmlFor="pa-prefissi">{t('Prefissi degli articoli da accettare')}</label>
                <input
                  className="input num"
                  id="pa-prefissi"
                  value={testo('bcArticleCodePrefixes')}
                  onChange={(e) => scrivi('bcArticleCodePrefixes')(e.target.value)}
                  placeholder="PFS,PFP,SLS,SLP,APL"
                />
                <span className="hint">
                  {t('Separati da virgola. In Business Central gli articoli sono molti di più — semilavorati, imballi — e senza questo filtro entrerebbero tutti.')}
                </span>
              </div>
              <div className="field">
                <label htmlFor="pa-sezionale">{t('Sezionale dei clienti creati dall’import')}</label>
                <select
                  className="select"
                  id="pa-sezionale"
                  value={valore('bcCustomerDefaultSectionalId') ?? ''}
                  onChange={(e) =>
                    cambia(
                      'bcCustomerDefaultSectionalId',
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                >
                  <option value="">{t('Non impostato')}</option>
                  {p.options.sectionals.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="pa-valuta">{t('Valuta dei clienti creati dall’import')}</label>
                <select
                  className="select"
                  id="pa-valuta"
                  value={valore('bcCustomerDefaultCurrency') ?? ''}
                  onChange={(e) =>
                    cambia('bcCustomerDefaultCurrency', e.target.value === '' ? null : e.target.value)
                  }
                >
                  <option value="">{t('Non impostato')}</option>
                  {p.options.currencies.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
              {t('Sezionale e valuta si mettono solo ai clienti che l’import crea da zero: a chi c’è già non si tocca niente.')}
            </p>
          </div>
        </div>
      </div>

      <div className="cantiere">
        <h2>{t('Quello che non sta più qui')}</h2>
        <p>
          {t('Nel programma di oggi questa pagina ha 78 voci, e quasi tutte sono nomi, chiavi e indirizzi dei collegamenti verso Business Central, i vettori e l’archivio dei file. Nella 3.0 sono configurazione del servizio: una password non sta in una tabella che si esporta, e cambiarla non deve essere una cosa che si fa per sbaglio da una maschera.')}
        </p>
      </div>
    </>
  );
}
